/*
 * album/nav.ts - the album's sticky bar (§5): Story | Grid toggle, the chapter strip with scroll spy
 * (IntersectionObserver) + smooth scroll, the favorites count. Also keeps two CSS custom properties on the
 * album root - the site nav's and the bar's height - so the bar sticks under the site nav and every chapter's
 * scroll-margin-top clears both.
 */

import { reduceMotion } from "./shared";

export type View = "story" | "grid";

export interface NavHooks {
  setView(v: View): void;
  view(): View;
  openFavorites(): void;
}

export function initAlbumNav(root: HTMLElement, hooks: NavHooks, favCount: () => number, onFavChange: (f: () => void) => void): { paintView(v: View): void } {
  const bar = root.querySelector<HTMLElement>(".album-bar");
  const siteNav = document.querySelector<HTMLElement>(".site-nav");

  // --- heights for sticky top + scroll-margin-top
  const measure = (): void => {
    if (siteNav) root.style.setProperty("--wf-album-nav-h", `${siteNav.offsetHeight}px`);
    if (bar) root.style.setProperty("--wf-album-bar-h", `${bar.offsetHeight}px`);
  };
  measure();
  window.addEventListener("resize", measure, { passive: true });
  if (siteNav && "ResizeObserver" in window) new ResizeObserver(measure).observe(siteNav);
  if (bar && "ResizeObserver" in window) new ResizeObserver(measure).observe(bar);

  // --- Story | Grid
  const toggles = Array.from(root.querySelectorAll<HTMLAnchorElement>("[data-album-view]"));
  toggles.forEach((a) =>
    a.addEventListener("click", (e) => {
      e.preventDefault();
      hooks.setView(a.dataset.albumView === "grid" ? "grid" : "story");
    }),
  );
  const paintView = (v: View): void => {
    toggles.forEach((a) => {
      const on = a.dataset.albumView === v;
      a.classList.toggle("is-active", on);
      a.setAttribute("aria-pressed", on ? "true" : "false");
    });
  };

  // --- chapter strip: smooth scroll + scroll spy
  const strip = root.querySelector<HTMLElement>("[data-album-strip]");
  const links = Array.from(root.querySelectorAll<HTMLAnchorElement>("[data-album-spy]"));
  const byId = new Map(links.map((a) => [a.dataset.albumSpy!, a]));

  links.forEach((a) =>
    a.addEventListener("click", (e) => {
      const id = a.dataset.albumSpy!;
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      if (hooks.view() !== "story") hooks.setView("story");
      target.scrollIntoView({ behavior: reduceMotion() ? "auto" : "smooth", block: "start" });
      history.replaceState(history.state, "", `#${id}`);
      activate(id);
    }),
  );

  let active = "";
  const activate = (id: string): void => {
    if (id === active) return;
    active = id;
    links.forEach((a) => {
      const on = a.dataset.albumSpy === id;
      a.classList.toggle("is-active", on);
      if (on) a.setAttribute("aria-current", "true");
      else a.removeAttribute("aria-current");
    });
    const link = byId.get(id);
    if (strip && link) {
      // the link's position inside the strip's scrolled content (offsetLeft would be relative to the sticky bar)
      const x = link.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
      const left = x - (strip.clientWidth - link.offsetWidth) / 2;
      strip.scrollTo({ left: Math.max(0, left), behavior: reduceMotion() ? "auto" : "smooth" });
    }
  };

  const sections = links
    .map((a) => document.getElementById(a.dataset.albumSpy!))
    .filter((s): s is HTMLElement => !!s);
  if (sections.length && "IntersectionObserver" in window) {
    const seen = new Set<Element>();
    let io: IntersectionObserver | null = null;
    const watch = (): void => {
      io?.disconnect();
      const top = (siteNav?.offsetHeight ?? 0) + (bar?.offsetHeight ?? 0) + 8;
      // a band from just under the bars to 45% down the screen: the first chapter in it is "where you are"
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => (en.isIntersecting ? seen.add(en.target) : seen.delete(en.target)));
          const first = sections.find((s) => seen.has(s));
          if (first) activate(first.id);
        },
        { rootMargin: `-${top}px 0px -55% 0px`, threshold: 0 },
      );
      sections.forEach((s) => io!.observe(s));
    };
    watch();
    window.addEventListener("resize", () => { seen.clear(); watch(); }, { passive: true });
  }

  // --- favorites count; the button opens the favorites sheet (AL6, album/favorites.ts)
  const favBtn = root.querySelector<HTMLButtonElement>("[data-album-fav-open]");
  const favNum = root.querySelector<HTMLElement>("[data-album-fav-count]");
  const paintFav = (): void => {
    const n = favCount();
    if (favNum) favNum.textContent = String(n);
    favBtn?.classList.toggle("has-favorites", n > 0);
  };
  paintFav();
  onFavChange(paintFav);
  favBtn?.addEventListener("click", () => hooks.openFavorites());

  return { paintView };
}
